import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { handleLogin } from "../../services/handlelogin";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useTheme } from "@/components/theme-provider";
import SEO from "@/shared/components/seo";

const loginSchema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(6, "Password is too short"),
});

function LoginPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async (data: { email?: string; password?: string }) =>
      handleLogin(data.email, data.password),
    onSuccess: () => {
      toast.success("Login successful!");
      navigate("/app/dashboard");
    },
    onError: (error) => {
      console.error("Login failed: ", error);
      toast.error("Login failed. Please check your credentials.");
    },
  });

  // Forms for login and register
  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof loginSchema>) => {
    if (values.email && values.password) {
      loginMutation.mutate(values);
    }
  };

  return (
    <div className="flex h-screen">
      <SEO
        title="Sign In"
        description="Access your MikroSell dashboard."
        robots="noindex,nofollow"
      />

      <div className="md:w-1/2  hidden md:flex items-center justify-center p-8">
        <img
          src={theme === "dark" ? "/darkmode.jpg" : "/lightmode.jpg"}
          alt="Login"
          className="w-full h-3/4 object-cover rounded-lg shadow-lg"
        />
      </div>

      <div className="w-full md:w-1/2 flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h1 className="text-2xl font-semibold mb-6">
            Sign in to your account
          </h1>

          <Form {...loginForm}>
            <form onSubmit={loginForm.handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 gap-6 p-6 border rounded-md">
                <FormField
                  control={loginForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={loginForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full py-2 px-4 rounded-lg bg-primary"
                >
                  {loginMutation.isPending ? "Signing in.." : "Sign in"}
                </Button>

                <Link
                  to="/reset-password-token"
                  className="text-sm text-center"
                >
                  Forgot Password?{" "}
                  <div className="ml-2 relative inline-block group">
                    <span className="text-primary hover:text-primary-dark">
                      Reset Password
                    </span>
                    <span className="absolute left-1/2 bottom-0 w-0 h-[1px] bg-primary transition-all duration-300 group-hover:w-full group-hover:left-0"></span>
                  </div>
                </Link>

                <div className="mb-2 text-sm text-center">
                  Don't have an account?{" "}
                  <Link
                    to="/sign-up"
                    className="mr-2 text-primary hover:text-primary-dark"
                  >
                    <div className="ml-2 relative inline-block group">
                      <span className="text-primary hover:text-primary-dark">
                        Sign up
                      </span>
                      <span className="absolute left-1/2 bottom-0 w-0 h-[1px] bg-primary transition-all duration-300 group-hover:w-full group-hover:left-0"></span>
                    </div>
                  </Link>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
